import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'react-query';
import { ASSET_DISPOSAL_API, ASSET_API } from '@app/utils/apiUrl';
import { Post, Get } from '@app/utils/httpProvider';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { ConfirmModal } from '../../modules/modal/ConfirmModal';
 

const AssetDisposalCreate = () => {
    const history = useHistory();

    const [title, setTitle] = useState('');
    const [notes, setNotes] = useState('');
    
    // Checkbox logic
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch toàn bộ tài sản Cấp 5 chưa bị đào thải
    const { isLoading, data: responseData } = useQuery(
        ['assetsForDisposal'],
        () => Get(ASSET_DISPOSAL_API.GET_CANDIDATES)
    );

    const candidates = responseData || [];

    const filteredCandidates = useMemo(() => {
        if (!searchTerm) return candidates;
        const lower = searchTerm.toLowerCase();
        return candidates.filter(item => 
            item.assetCode?.toLowerCase().includes(lower) || 
            item.assetName?.toLowerCase().includes(lower)
        );
    }, [candidates, searchTerm]);

    const isAllChecked = filteredCandidates.length > 0 && filteredCandidates.every((item) => selectedIds.has(item.assetId));

    const handleToggleCheckAll = () => {
        if (isAllChecked) {
            setSelectedIds(new Set());
        } else {
            const allIds = new Set(filteredCandidates.map(c => c.assetId));
            setSelectedIds(allIds);
        }
    };

    const handleToggleRow = (assetId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(assetId)) {
                next.delete(assetId);
            } else {
                next.add(assetId);
            }
            return next;
        });
    };

    // 3. Create mutation
    const createMutation = useMutation((payload) =>
        Post(ASSET_DISPOSAL_API.ADD_ITEM, null, payload)
    );

    const handleConfirmSubmit = () => {
        if (!title.trim()) {
            toast.warn('Vui lòng nhập tiêu đề phiếu đào thải');
            return;
        }

        if (selectedIds.size === 0) {
            toast.warn('Vui lòng chọn ít nhất một tài sản để đào thải');
            return;
        }

        setOpenConfirmModal(true);
    };

    const handleAcceptSubmit = () => {
        const payload = {
            title: title.trim(),
            notes: notes.trim(),
            assetIds: Array.from(selectedIds)
        };

        createMutation.mutate(payload, {
            onSuccess: (res) => {
                toast.success('Chốt phiếu đào thải thành công! Các tài sản đã chuyển sang trạng thái Đào thải.');
                history.push(URI_INTERNAL.ASSET_DISPOSAL_PATH);
            },
            onError: (err) => {
                toast.error('Có lỗi xảy ra khi tạo phiếu đào thải');
                console.error(err);
            }
        });
        setOpenConfirmModal(false);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white py-3 d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center">
                            <h5 className="card-title fw-bold text-danger mb-2 mb-md-0">
                                <i className="fas fa-trash mr-2" />
                                Lập Phiếu Đào Thải Tài Sản
                            </h5>
                            <div className="d-flex align-items-center mt-2 mt-md-0 ml-md-auto">
                                <button type="button" className="btn btn-sm btn-secondary mr-2" onClick={() => history.push(URI_INTERNAL.ASSET_DISPOSAL_PATH)}>
                                    <i className="fas fa-arrow-left mr-1"></i> Quay lại
                                </button>
                                <button type="button" className="btn btn-sm btn-primary" onClick={handleConfirmSubmit} disabled={createMutation.isLoading}>
                                    {createMutation.isLoading ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-save mr-1"></i>}
                                    Chốt phiếu
                                </button>
                            </div>
                        </div>
                        <div className="card-body bg-light">
                            <div className="row">
                                {/* Form Information */}
                                <div className="col-12 mb-4">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <h6 className="font-weight-bold mb-3 text-primary border-bottom pb-2">
                                                <i className="fas fa-info-circle mr-2"></i> Thông tin chung
                                            </h6>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="form-group mb-3">
                                                        <label className="font-weight-bold text-secondary">Tiêu đề phiếu <span className="text-danger">*</span></label>
                                                        <input 
                                                            type="text" 
                                                            className="form-control" 
                                                            placeholder="VD: Đào thải tài sản hỏng tháng 10" 
                                                            value={title}
                                                            onChange={e => setTitle(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-group mb-3">
                                                        <label className="font-weight-bold text-secondary">Lý do / Ghi chú</label>
                                                        <textarea 
                                                            className="form-control" 
                                                            rows="2" 
                                                            placeholder="Nhập lý do chi tiết..."
                                                            value={notes}
                                                            onChange={e => setNotes(e.target.value)}
                                                            style={{ resize: 'none' }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Asset Selection Table */}
                                <div className="col-12 mb-4">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom pb-2 mb-3">
                                                <h6 className="font-weight-bold text-primary mb-2 mb-sm-0">
                                                    <i className="fas fa-list-check mr-2"></i> Chọn tài sản đào thải
                                                    <span className="badge badge-danger ml-2">{selectedIds.size} đã chọn</span>
                                                </h6>
                                                <div className="input-group w-100 w-sm-auto" style={{ maxWidth: '300px' }}>
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-sm" 
                                                        placeholder="Tìm mã, tên tài sản..." 
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                    <div className="input-group-append">
                                                        <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                                                    </div>
                                                </div>
                                            </div>

                                            {isLoading ? <div className="text-center p-4"><i className="fas fa-spinner fa-spin fa-2x text-primary"></i></div> : (
                                                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                    <table className="table table-striped table-bordered table-hover table-sm mobile-card-view">
                                                        <thead className="thead-light sticky-top">
                                                                <tr>
                                                                    <th className="text-center" style={{ width: '40px' }}>
                                                                        <div className="custom-control custom-checkbox">
                                                                            <input 
                                                                                type="checkbox" 
                                                                                className="custom-control-input" 
                                                                                id="checkAll" 
                                                                                checked={isAllChecked}
                                                                                onChange={handleToggleCheckAll}
                                                                            />
                                                                            <label className="custom-control-label" htmlFor="checkAll"></label>
                                                                        </div>
                                                                    </th>
                                                                    <th>Mã tài sản</th>
                                                                    <th>Tên tài sản</th>
                                                                    <th>Khoa phòng</th>
                                                                    <th className="text-center">Cấp CL</th>
                                                                    <th className="text-center">Trạng thái</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredCandidates.length > 0 ? filteredCandidates.map(item => (
                                                                <tr key={item.assetId} onClick={() => handleToggleRow(item.assetId)} style={{ cursor: 'pointer' }}>
                                                                    <td className="text-center" onClick={e => e.stopPropagation()} data-label="Chọn">
                                                                        <div className="custom-control custom-checkbox">
                                                                            <input 
                                                                                type="checkbox" 
                                                                                className="custom-control-input" 
                                                                                id={`check_${item.assetId}`} 
                                                                                checked={selectedIds.has(item.assetId)}
                                                                                onChange={() => handleToggleRow(item.assetId)}
                                                                            />
                                                                            <label className="custom-control-label" htmlFor={`check_${item.assetId}`}></label>
                                                                        </div>
                                                                    </td>
                                                                    <td className="font-weight-bold text-primary" data-label="Mã tài sản">{item.assetCode}</td>
                                                                    <td data-label="Tên tài sản">{item.assetName}</td>
                                                                    <td data-label="Khoa phòng">{item.departmentName || '-'}</td>
                                                                    <td className="text-center font-weight-bold text-danger" data-label="Cấp CL">{item.qualityLevelName || '-'}</td>
                                                                    <td className="text-center" data-label="Trạng thái">
                                                                        <span className="badge badge-warning text-dark px-2 py-1" style={{ borderRadius: '4px' }}>
                                                                            {item.statusName || 'Bình thường'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            )) : (
                                                                <tr>
                                                                    <td colSpan="6" className="text-center py-4 text-muted">
                                                                        Không tìm thấy tài sản nào hợp lệ.
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Warning Alert */}
                                <div className="col-12 mb-3">
                                    <div className="alert alert-warning shadow-sm m-0">
                                        <i className="fas fa-exclamation-triangle mr-2"></i>
                                        <strong>Lưu ý:</strong> Tài sản sau khi đào thải sẽ <strong>không thể</strong> tiếp tục sử dụng hay xuất hiện trong các chức năng kiểm kê, sửa chữa, điều chuyển nữa.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={openConfirmModal}
                confirmType="save"
                variant="danger"
                acceptText="Chốt Phiếu"
                handleAccept={handleAcceptSubmit}
                handleCancel={() => setOpenConfirmModal(false)}
                title="Xác nhận chốt phiếu đào thải"
                content={`Bạn đang chuẩn bị chốt đào thải ${selectedIds.size} tài sản. Thao tác này sẽ thiết lập các tài sản đã chọn thành trạng thái "Đã đào thải" và không thể hoàn tác nếu không thông qua Hủy phiếu. Bạn có chắc chắn tiếp tục?`}
            />
        </div>
    );
};

export default AssetDisposalCreate;
